// Shared display formats, so every page shows dates, numbers and enum values the same way.

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const shortDateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const monthYearFormat = new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit' });
const timeFormat = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

/** Shown wherever a value is missing. */
export const EMPTY = '—';

/** "1 Aug 2026". Returns the fallback for empty or unparseable values. */
export const formatDate = (value, fallback = EMPTY) => {
  const date = toDate(value);
  return date ? dateFormat.format(date) : fallback;
};

/** "1 Aug 2026, 14:03". */
export const formatDateTime = (value, fallback = EMPTY) => {
  const date = toDate(value);
  return date ? `${dateFormat.format(date)}, ${timeFormat.format(date)}` : fallback;
};

/** "3 Aug", for chart axes and compact lists. */
export const formatShortDate = (value, fallback = EMPTY) => {
  const date = toDate(value);
  return date ? shortDateFormat.format(date) : fallback;
};

/** "Mar 24", for month buckets such as "2024-03". */
export const formatMonthYear = (value, fallback = EMPTY) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) {
    const [year, month] = value.split('-').map(Number);
    return monthYearFormat.format(new Date(year, month - 1, 1));
  }
  const date = toDate(value);
  return date ? monthYearFormat.format(date) : fallback;
};

/** "14:03". */
export const formatTime = (value, fallback = EMPTY) => {
  const date = toDate(value);
  return date ? timeFormat.format(date) : fallback;
};

/** A local calendar date as "2026-08-01", without the UTC shift toISOString() causes in summer time. */
export const toLocalIsoDate = (date) => {
  const d = toDate(date);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** "1,861". */
export const formatNumber = (value, fallback = EMPTY) =>
  (value === null || value === undefined || Number.isNaN(Number(value)) ? fallback : Number(value).toLocaleString('en-GB'));

/** "1 user", "2 users". */
export const plural = (count, singular, pluralForm = `${singular}s`) =>
  `${formatNumber(count)} ${count === 1 ? singular : pluralForm}`;

/** "FREE_TRIAL" reads as "Free trial". */
export const humanizeEnum = (value, fallback = EMPTY) => {
  if (!value) return fallback;
  const text = String(value).replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const ROLE_LABELS = {
  COMPANY_ADMIN: 'Company admin',
  DEPARTMENT_ADMIN: 'Department admin',
  OFFICE_ADMIN: 'Office admin',
  TEAM_ADMIN: 'Team admin',
  BASIC: 'Basic',
};

/** A user's role in their company. */
export const roleLabel = (role, fallback = EMPTY) => ROLE_LABELS[role] || humanizeEnum(role, fallback);

const AUTH_TYPE_LABELS = {
  TT: 'Email and password',
  GG: 'Google',
  MS: 'Microsoft',
};

/** How a user signs in. */
export const authTypeLabel = (authType, fallback = EMPTY) => AUTH_TYPE_LABELS[authType] || humanizeEnum(authType, fallback);

/** Approval statuses such as PENDING read as "Pending". */
export const statusLabel = (status, fallback = EMPTY) => humanizeEnum(status, fallback);
